/*
 * Copyright (C) 2023 - present Marek Kuzora - All Rights Reserved.
 */


import EntityEnum from 'astro/enum/entity';
import ParentEnum from 'astro/enum/parent';

import Cache from 'fierry/flux/cache';
import Model from 'fierry/rpc.old/model';

import PlanetAttributes from 'astro/server/planet/attributes';
import PlanetInstaller from 'astro/server/planet/installer';

import Sector from 'astro/server/sector';
import User from 'astro/server/user/local';

import EntityCollection from 'astro/server/entity/collection';
import SectorCollection from 'astro/server/sector/collection';
import ZoneCollection from 'astro/server/zone/collection';

import InteractionCollection from 'astro/server/interaction/collection';
import ProjectileCollection from 'astro/server/projectile/collection';
import LocalUserCollection from 'astro/server/user/local.collection';

import Dock from 'astro/server/zone/dock';

import { makeLocation } from 'astro/server/location';

import * as Coords from 'shared/geo/coords';
import * as Shapes from 'shared/geo/shapes';
import * as Array from 'fierry/util/array';


export default class extends Model
{
  constructor (state, hierarchy)
  {
    super(state, hierarchy);

    this.attributes = new PlanetAttributes(this);

    this.interactions = new InteractionCollection(state);
    this.projectiles = new ProjectileCollection(this, state);

    this.zones = new ZoneCollection(this, state.get('zones'));
    this.users = new LocalUserCollection(this, state.get('users'));
    this.sectors = new SectorCollection(this, state.get('sectors'));
    this.entities = new EntityCollection(this, state.get('entities'));

    this.entities.setup();
    this.zones.setup();

    this.__createDocks__();

    this.install();
  }


  install ()
  {
    const installer = new PlanetInstaller(this);

    this.entities.setupInstaller(installer);
    this.interactions.setupInstaller(installer);

    installer.execute();

    this.sectors.install();
    this.zones.install();
  }


  __createDocks__ ()
  {
    this.docks = new Map();

    for (const zone of this.getZones().getAll())
    {
      if (zone.isPlatform())
      {
        continue;
      }

      for (const coords of Coords.getNeighborsInRange(zone.getCoords()))
      {
        if (this.getZone(coords) == null && !this.docks.has(coords.toString()))
        {
          this.docks.set(coords.toString(), new Dock(coords, this))
        }
      }
    }
  }


  getAttributes ()
  {
    return this.attributes;
  }


  getInteractions ()
  {
    return this.interactions;
  }


  getProjectiles ()
  {
    return this.projectiles;
  }


  getEntities ()
  {
    return this.entities;
  }


  getSectors ()
  {
    return this.sectors;
  }


  getZones ()
  {
    return this.zones;
  }


  getUsers ()
  {
    return this.users;
  }


  getBuilding (zoneID)
  {
    return this.entities.getBuilding(zoneID);
  }


  getUnit (coords)
  {
    return this.entities.getUnit(coords.toString());
  }


  getUser (id)
  {
    return this.users.getUser(id);
  }


  getZone (coords, parent)
  {
    if (parent && (parent.type !== ParentEnum.Planet || parent.id !== this.getID()))
    {
      return null;
    }

    return this.getZones().getOne(coords);
  }


  getDock (coords, parent)
  {
    if (parent && (parent.type !== ParentEnum.Planet || parent.id !== this.getID()))
    {
      return null;
    }

    return this.docks.get(coords.toString());
  }


  getPlatform (coords, parent)
  {
    if (parent && (parent.type !== ParentEnum.Planet || parent.id !== this.getID()))
    {
      return null;
    }

    return this.getZones().getPlatform(coords);
  }


  getLocation (coordsOrLocation)
  {
    return makeLocation(coordsOrLocation, this);
  }


  eachZone (center, shape, callback)
  {
    for (const coordinate of Shapes.getCoords(center.id, shape))
    {
      callback(this.getZone(coordinate));
    }
  }


  isWithinBounds (coords, delta = 0)
  {
    const { id, x, y, width, height } = this.getCass();

    return (x - delta) <= coords.getX() && coords.getX() <= (x + width + delta) && (y - delta) <= coords.getY() && coords.getY() <= (y + height + delta);
  }


  getConstants ()
  {
    return this.state.get('constants');
  }


  getCass ()
  {
    return this.state.get('currentPlanet');
  }


  getID ()
  {
    return this.getCass().id;
  }


  getCoords ()
  {
    return this.getCass().coords;
  }


  isLocal ()
  {
    return true;
  }


  // Unverified API


  getSectorNeighbors (userID)
  {
    let neighbors = [];

    for (const lhs of this.sectors.getAll())
    {
      if (lhs.getUser().getID() !== userID)
      {
        continue;
      }

      for (const rhs of this.sectors.getAll())
      {
        if (rhs.getUser().getID() === userID)
        {
          continue;
        }

        if (lhs.getNeighbors().isNeighbor(rhs))
        {
          neighbors.push(rhs);
        }
      }
    }

    return Array.unique(neighbors);
  }


}
